// Lógica pura do filtro por coluna das tabelas (sem antd/React, testável no jest).
// A parte visual está em filtrosColuna.tsx.

/** Minúsculas e sem acento: "São Paulo" casa com "sao paulo". */
export function normalizar(v: unknown): string {
  if (v == null) return '';
  return String(v).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** O texto digitado aparece em qualquer parte do valor? Busca vazia casa tudo. */
export function casaTexto(valor: unknown, busca: unknown): boolean {
  const b = normalizar(busca);
  if (!b) return true;
  return normalizar(valor).includes(b);
}

/** Número vindo do Postgres (string) ou do JS. Vazio/inválido vira null. */
export function paraNumero(v: unknown): number | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

/** Faixa numérica inclusiva. Sem limite = aberto. Valor vazio nunca casa se houver limite. */
export function casaFaixaNumero(valor: unknown, min: number | null | undefined, max: number | null | undefined): boolean {
  const temMin = min != null && Number.isFinite(min);
  const temMax = max != null && Number.isFinite(max);
  if (!temMin && !temMax) return true;
  const n = paraNumero(valor);
  if (n == null) return false;
  if (temMin && n < (min as number)) return false;
  if (temMax && n > (max as number)) return false;
  return true;
}

/** 'YYYY-MM-DD' (ou ISO) → só a parte da data. Sem fuso: fatia o texto. */
export function soData(v: unknown): string | null {
  if (v == null || v === '') return null;
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

/** Limite da faixa em 'YYYY-MM-DD': aceita ISO e também 'DD/MM/AAAA' (o texto do calendário). */
export function limiteData(v: unknown): string | null {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  return soData(s);
}

/** Faixa de datas inclusiva nas duas pontas. Valor 'YYYY-MM-DD' (ou ISO); limites ISO ou 'DD/MM/AAAA'. */
export function casaFaixaData(valor: unknown, de: string | null | undefined, ate: string | null | undefined): boolean {
  const ini = limiteData(de);
  const fim = limiteData(ate);
  if (!ini && !fim) return true;
  const d = soData(valor);
  if (!d) return false;
  if (ini && d < ini) return false;
  if (fim && d > fim) return false;
  return true;
}

/** Valores distintos, sem vazios, em ordem alfabética pt-BR (para filtros de opção). */
export function valoresDistintos(valores: unknown[]): string[] {
  const set = new Set<string>();
  for (const v of valores) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) set.add(s);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
}

export interface Estatisticas {
  quantidade: number; // linhas consideradas (todas, mesmo as sem valor)
  comValor: number;   // linhas com valor numérico
  soma: number;
  media: number;      // 0 quando não há valores
  minimo: number | null;
  maximo: number | null;
}

/** Soma, média, menor e maior de uma coluna; ignora nulos e vazios. Não arredonda. */
export function estatisticas<T>(linhas: readonly T[], getValor: (l: T) => unknown): Estatisticas {
  let soma = 0;
  let comValor = 0;
  let minimo: number | null = null;
  let maximo: number | null = null;
  for (const l of linhas) {
    const n = paraNumero(getValor(l));
    if (n == null) continue;
    soma += n;
    comValor += 1;
    if (minimo == null || n < minimo) minimo = n;
    if (maximo == null || n > maximo) maximo = n;
  }
  return {
    quantidade: linhas.length,
    comValor,
    soma,
    media: comValor ? soma / comValor : 0,
    minimo,
    maximo,
  };
}
